import { Button } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import styled from 'styled-components';

import FuelActionbar from '@/components/fuel-actionbar';
import FuelTable from '@/components/fuel-table/FuelTable';
import { useProgress } from '@/contexts/ProgressProvider';
import { useModalContext } from '@/modals/VNModalProvider';
import type { Pagination } from '@/models';
import useAppUtils from '@/utils/appUtils';
import clientUtils from '@/utils/clientUtils';

import VnModalBody from '../VnModalBody';
import VnModalFooter from '../VnModalFooter';

export const initialTaskPagination = {
  sortBy: 'lastUpdatedDate',
  descending: true,
  rowsPerPage: 100,
  page: 1,
  totalItems: 0,
  pages: 1,
};

const TableContent = styled.div`
  height: 480px;
`;

interface IProps {
  items: [];
  pagination: any;
  feedTaskId?: string;
  onDeleteFeedItem?: (feedItemId) => void;
}

const ShowFeedTaskItemsModal = ({ items, pagination, onDeleteFeedItem = null }: IProps) => {
  const columnHelper = createColumnHelper<any>();
  const { alert } = useAppUtils();
  const { loading } = useProgress();
  const { onClose } = useModalContext();

  const columns = [
    columnHelper.accessor('feedItemId', {
      align: 'left',
      header: () => 'Item Id',
      width: 250,
      sortable: true,
      cell: (cell) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px' }}>
            <span
              className="feedItemId-data"
              title={cell.row.original.displayFeedItemId}
              style={{ width: '320px', textOverflow: 'ellipsis', overflow: 'hidden' }}
            >
              {cell.row.original.feedItemId}
            </span>
            {cell.row.original.displayFeedItemId !== null && (
              <span className="copy-button icon">
                <i
                  aria-hidden="true"
                  className="fa fa-clone"
                  onClick={(event) => {
                    clientUtils.copyToClipboard(cell.row.original.feedItemId);
                    alert(`Item Id ${cell.row.original.feedItemId} copied.`);
                  }}
                />
              </span>
            )}
          </div>
        );
      },
    }),
    columnHelper.accessor('createdClipId', {
      header: () => <span>Created Clip Id</span>,
      cell: (cell) => {
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '2px' }}>
            <span className="createdClipId-data" title={cell.row.original.createdClipId}>
              {cell.row.original.createdClipId}
            </span>
            {cell.row.original.createdClipId !== null && (
              <span className="copy-button icon">
                <i
                  aria-hidden="true"
                  className="fa fa-clone"
                  onClick={(event) => {
                    clientUtils.copyToClipboard(cell.row.original.createdClipId);
                    alert(`Clip Id ${cell.row.original.createdClipId} copied.`);
                  }}
                />
              </span>
            )}
          </div>
        );
      },
      width: 250,
      sortable: false,
    }),
    columnHelper.accessor('lastUpdatedDate', {
      header: 'Last Updated',
      footer: (props) => props.column.id,
      width: 130,
      sortable: false,
      cell: (cell) => clientUtils.formatUTCDateTime(cell.getValue()),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      footer: (props) => props.column.id,
      sortable: false,
      width: 150,
      cell: (cell) => <div title={cell.getValue()}>{cell.getValue()}</div>,
    }),
    columnHelper.accessor('actions', {
      header: 'Actions',
      footer: (props) => props.column.id,
      width: 50,
      sortable: false,
      cell: (cell) => (
        <FuelActionbar actions={cell.row.original.actions} item={cell.row.original} onActionButtonClick={onTableAction} />
      ),
    }),
  ];

  const paginationChange = (paginationDetail: Pagination) => {};
  const onTableAction = async (event, type, feedTask: any, context: any) => {
    // console.log('Action', {type, feedTask})
    if (type === 'delete' && onDeleteFeedItem) {
      onDeleteFeedItem(feedTask.feedItemId);
    }
  };

  return (
    <div className="full-height">
      <VnModalBody>
        <TableContent>
          <FuelTable
            columnHelper={columnHelper}
            data={items}
            defaultColumns={columns}
            paginationDetail={pagination}
            paginationChange={paginationChange}
            showRowSelection={false}
            loading={loading}
            tableHeight={60}
            showActionbar
            hideActionbarButtons
            hideSearchControl
          />
        </TableContent>
      </VnModalBody>
      <VnModalFooter
        right={
          <Button autoFocus variant="contained" className="cancel-button" onClick={onClose}>
            Close
          </Button>
        }
      />
    </div>
  );
};

export default ShowFeedTaskItemsModal;
